// Keep a usable preview on screen until the full photograph has decoded.
window.DDProductImages = window.DDProductImages || (() => {
  const ready = new Set();
  let slowPreview = false;
  const constrained = () => {
    const connection = navigator.connection;
    return navigator.onLine === false || slowPreview || Boolean(connection && (
      connection.saveData || /(^|-)2g$|^3g$/.test(connection.effectiveType || '') ||
      (connection.downlink > 0 && connection.downlink < 1.5)
    ));
  };
  const notePreview = (milliseconds) => { if (milliseconds > 1800) slowPreview = true; };
  const load = (url, { signal, timeout = 12000, priority = 'low' } = {}) => new Promise((resolve, reject) => {
    const image = new Image();
    let finished = false;
    const finish = (error) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      image.onload = image.onerror = null;
      if (error) { image.removeAttribute('src'); reject(error); }
      else resolve(image);
    };
    const abort = () => finish(new DOMException('Image request cancelled', 'AbortError'));
    const timer = setTimeout(() => finish(new Error('Image request timed out')), timeout);
    image.decoding = 'async';
    image.fetchPriority = priority;
    image.onload = async () => {
      try { if (image.decode) await image.decode(); finish(); }
      catch (error) { finish(error); }
    };
    image.onerror = () => finish(new Error('Image could not load'));
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
    else image.src = url;
  });

  const initialise = (root = document) => {
    root.querySelectorAll('modal-opener img[data-dd-full-src]').forEach((image) => {
      if (image.dataset.ddImageReady || !image.dataset.ddFullSrc) return;
      image.dataset.ddImageReady = 'true';
      image.dataset.ddQuality = 'preview';
      let visible = false, controller, timer, fallbackUsed = false, attempted = false;
      const full = image.dataset.ddFullSrc;
      const fallback = image.dataset.ddFallbackSrc;
      const upgrade = async () => {
        if (!visible || !image.isConnected || !image.naturalWidth || controller || attempted ||
            image.dataset.ddQuality === 'full' || image.getBoundingClientRect().width < 300 ||
            (!ready.has(full) && constrained())) return;
        attempted = true;
        const request = controller = new AbortController();
        try {
          await load(full, { signal: request.signal });
          if (request.signal.aborted || !image.isConnected) return;
          ready.add(full);
          image.removeAttribute('sizes');
          image.srcset = `${full} 1x`;
          image.src = full;
          image.dataset.ddQuality = 'full';
        } catch (error) {
          if (error.name === 'AbortError') attempted = false;
          // The preview remains visible when the detail request fails.
        } finally { if (controller === request) controller = null; }
      };
      const useFallback = () => {
        clearTimeout(timer);
        if (fallbackUsed || !fallback || image.dataset.ddQuality === 'full') return;
        fallbackUsed = true;
        slowPreview = true;
        image.removeAttribute('sizes');
        image.srcset = `${fallback} 1x`;
        image.src = fallback;
      };
      const loaded = () => {
        clearTimeout(timer);
        if (image.dataset.ddQuality !== 'full') {
          const timings = performance.getEntriesByName(image.currentSrc);
          if (timings.length) notePreview(timings[timings.length - 1].duration);
        }
        upgrade();
      };
      image.addEventListener('load', loaded);
      image.addEventListener('error', useFallback);
      if ('IntersectionObserver' in window) {
        const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(upgrade) : null;
        resizeObserver?.observe(image);
        const observer = new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          clearTimeout(timer);
          if (visible) {
            if (image.complete && image.naturalWidth) loaded();
            else if (image.complete) useFallback();
            else timer = setTimeout(useFallback, 7000);
          } else { controller?.abort(); }
        });
        observer.observe(image);
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target.contains(image)) {
            observer.disconnect(); resizeObserver?.disconnect(); controller?.abort(); clearTimeout(timer);
          }
        });
      } else {
        visible = true;
        if (image.complete && image.naturalWidth) loaded();
      }
      window.addEventListener('online', () => {
        if (!visible || !image.isConnected) return;
        if (!image.naturalWidth) { fallbackUsed = false; useFallback(); }
        else { attempted = false; upgrade(); }
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  return { load, ready, constrained, notePreview };
})();

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      async show(opener) {
        if (this.gallery || this.galleryLoading) return;
        this.openedBy = opener;
        const mediaId = opener.getAttribute('data-media-id');
        // Read afresh: selecting a variant may reorder the modal's media.
        const images = Array.from(this.querySelectorAll('.product-media-modal__content > img[data-media-id]'));
        const selected = images.find((image) => image.dataset.mediaId === mediaId);
        if (!selected || !this.dataset.galleryModule) {
          this.showLegacy(opener);
          return;
        }
        const visibleImages = images.filter((image, index) =>
          image === selected || index === 0 || !image.classList.contains('product__media-item--variant')
        );
        const items = visibleImages.map((image) => {
          const width = Number(image.dataset.zoomWidth);
          const full = image.dataset.zoomSrc;
          const pageImage = Array.from(document.querySelectorAll('modal-opener')).find((element) =>
            element.dataset.modal === `#${this.id}` &&
            element.querySelector('button[data-media-id]')?.dataset.mediaId === image.dataset.mediaId
          )?.querySelector('img');
          const cachedPreview = pageImage?.complete && pageImage.naturalWidth ? pageImage.currentSrc : '';
          return {
            src: window.DDProductImages.ready.has(full) ? full : cachedPreview || image.dataset.previewSrc,
            msrc: cachedPreview,
            ddFullSrc: full,
            ddFallbackSrc: image.dataset.fallbackSrc,
            width,
            height: Number(image.dataset.zoomHeight),
            alt: image.alt,
          };
        });
        if (items.some((item) => !item.src || !item.width || !item.height)) {
          this.showLegacy(opener);
          return;
        }
        this.galleryLoading = true;
        const request = this.galleryRequest = {};
        opener.setAttribute('aria-busy', 'true');
        const cancelPending = (event) => { if (event.key === 'Escape') this.hide(); };
        document.addEventListener('keydown', cancelPending);
        try {
          const { default: PhotoSwipe } = await import(this.dataset.galleryModule);
          if (this.galleryRequest !== request || !this.isConnected || !opener.isConnected) return;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const maximumZoom = (level) => Math.max(level.fit, Math.min(1, level.fit * 6));
          const gallery = new PhotoSwipe({
            dataSource: items,
            index: visibleImages.indexOf(selected),
            mainClass: 'dd-product-zoom',
            bgOpacity: 1,
            showHideAnimationType: 'fade',
            showAnimationDuration: reducedMotion ? 0 : 180,
            hideAnimationDuration: reducedMotion ? 0 : 160,
            zoomAnimationDuration: reducedMotion ? 0 : 200,
            initialZoomLevel: 'fit',
            secondaryZoomLevel: (level) => Math.min(maximumZoom(level), level.fit * 2.5),
            maxZoomLevel: maximumZoom,
            wheelToZoom: true,
            pinchToClose: false,
            closeOnVerticalDrag: false,
            allowPanToNext: false,
            imageClickAction: 'zoom',
            bgClickAction: false,
            tapAction: false,
            doubleTapAction: 'zoom',
            zoom: false,
            returnFocus: false,
            loop: false,
            preload: [0, 0],
            paddingFn: (viewport) => ({
              top: 76, bottom: viewport.x < 750 ? 126 : 106,
              left: viewport.x < 750 ? 12 : 72, right: viewport.x < 750 ? 12 : 72,
            }),
            closeTitle: 'Close photos',
            arrowPrevTitle: 'Previous photo',
            arrowNextTitle: 'Next photo',
            errorMsg: 'This photo could not load. Check your connection, then reopen the photo.',
          });
          this.gallery = gallery;
          this.installAdaptiveImages(gallery);
          this.installGalleryControls(gallery, reducedMotion);
          const bodyWasLocked = document.body.classList.contains('overflow-hidden');
          document.body.classList.add('overflow-hidden', 'dd-product-zoom-open');
          window.pauseAllMedia();
          gallery.on('afterInit', () => {
            gallery.element.setAttribute('aria-label', 'Product photos');
            gallery.element.setAttribute('aria-modal', 'true');
          });
          gallery.on('destroy', () => {
            this.gallery = null;
            document.body.classList.remove('dd-product-zoom-open');
            if (!bodyWasLocked) document.body.classList.remove('overflow-hidden');
            document.body.dispatchEvent(new CustomEvent('modalClosed'));
            if (opener.isConnected) opener.focus({ preventScroll: true });
          });
          gallery.init();
        } catch (error) {
          if (this.gallery) this.gallery.destroy();
          if (this.galleryRequest === request && opener.isConnected) this.showLegacy(opener);
          console.warn('Product photo viewer could not load; using the standard viewer.', error);
        } finally {
          document.removeEventListener('keydown', cancelPending);
          opener.removeAttribute('aria-busy');
          this.galleryLoading = false;
        }
      }

      installAdaptiveImages(gallery) {
        const delivery = window.DDProductImages;
        const states = new Map();
        let closed = false;
        const stateFor = (content) => {
          if (!states.has(content)) states.set(content, { ready: false, full: false, attempted: false });
          return states.get(content);
        };
        const active = (content) => !closed && content.index === gallery.currIndex;
        const upgrade = async (content, explicit = false) => {
          if (!content || !active(content)) return;
          const state = stateFor(content);
          if (explicit) state.wantsFull = true;
          if (!state.ready || state.full || state.detail || state.attempted ||
              (!state.wantsFull && !delivery.ready.has(content.data.ddFullSrc) && delivery.constrained())) return;
          state.attempted = true;
          const request = state.detail = new AbortController();
          try {
            await delivery.load(content.data.ddFullSrc, {
              signal: request.signal, timeout: explicit ? 20000 : 12000, priority: explicit ? 'high' : 'low',
            });
            if (!active(content) || request.signal.aborted) return;
            delivery.ready.add(content.data.ddFullSrc);
            // Only replace the pixels: preserve the customer's zoom and pan.
            content.element.src = content.data.ddFullSrc;
            state.full = true;
          } catch (error) {
            if (error.name === 'AbortError') state.attempted = false;
          } finally {
            if (state.detail === request) state.detail = null;
          }
        };
        const showPreview = async (content) => {
          if (!active(content) || !content.element) return;
          const state = stateFor(content);
          if (state.base || state.ready) { upgrade(content); return; }
          const request = state.base = new AbortController();
          content.state = 'loading';
          let source = content.data.src;
          const started = performance.now();
          try {
            try { await delivery.load(source, { signal: request.signal, timeout: 7000, priority: 'high' }); }
            catch (error) {
              if (error.name === 'AbortError' || !content.data.ddFallbackSrc || source === content.data.ddFallbackSrc) throw error;
              source = content.data.ddFallbackSrc;
              await delivery.load(source, { signal: request.signal, timeout: 12000, priority: 'high' });
            }
            if (!active(content) || request.signal.aborted) return;
            delivery.notePreview(performance.now() - started);
            content.element.alt = content.data.alt || '';
            content.element.src = source;
            state.ready = true;
            state.full = source === content.data.ddFullSrc;
            content.onLoaded();
          } catch (error) {
            if (error.name === 'AbortError') content.state = 'idle';
            else if (active(content)) content.onError();
          } finally {
            if (state.base === request) state.base = null;
            if (state.ready && active(content)) upgrade(content, state.wantsFull);
            else if (request.signal.aborted && active(content)) showPreview(content);
          }
        };
        const cancel = (content) => {
          const state = states.get(content);
          state?.base?.abort();
          state?.detail?.abort();
        };
        const retryPreview = (content) => {
          cancel(content);
          states.delete(content);
          content.remove();
          content.load(false, true);
        };
        gallery.on('contentLoadImage', (event) => {
          event.preventDefault();
          // PhotoSwipe creates adjacent slides; download only the active photo.
          showPreview(event.content);
        });
        gallery.on('contentActivate', ({ content }) => {
          if (content.state === 'error') retryPreview(content);
          else showPreview(content);
        });
        gallery.on('contentDeactivate', ({ content }) => cancel(content));
        gallery.on('contentDestroy', ({ content }) => { cancel(content); states.delete(content); });
        gallery.on('zoomPanUpdate', () => {
          const slide = gallery.currSlide;
          if (slide && slide.currZoomLevel > slide.zoomLevels.initial + 0.001) upgrade(slide.content, true);
        });
        gallery.on('close', () => { closed = true; states.forEach((_, content) => cancel(content)); });
        gallery.on('destroy', () => { closed = true; states.forEach((_, content) => cancel(content)); states.clear(); });
      }

      installGalleryControls(gallery, reducedMotion) {
        gallery.on('uiRegister', () => {
          gallery.ui.registerElement({
            name: 'dd-controls',
            appendTo: 'root',
            onInit: (element) => {
              element.className = 'dd-zoom-controls';
              const help = document.createElement('p');
              help.className = 'dd-zoom-help';
              help.textContent = window.matchMedia('(pointer: coarse)').matches
                ? 'Pinch or double-tap to zoom · Swipe when zoomed out'
                : 'Scroll or click to zoom · Drag to explore';
              const bar = document.createElement('div');
              bar.className = 'dd-zoom-toolbar';
              bar.setAttribute('role', 'group');
              bar.setAttribute('aria-label', 'Photo zoom');
              const createButton = (label, text, action) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.setAttribute('aria-label', label);
                button.textContent = text;
                button.addEventListener('click', action);
                bar.appendChild(button);
                return button;
              };
              const zoom = (factor) => {
                const slide = gallery.currSlide;
                if (!slide) return;
                const level = factor === 0 ? slide.zoomLevels.initial :
                  Math.max(slide.zoomLevels.initial, Math.min(slide.zoomLevels.max, slide.currZoomLevel * factor));
                gallery.zoomTo(level, undefined, reducedMotion ? 0 : 180);
              };
              const minus = createButton('Zoom out', '−', () => zoom(1 / 1.5));
              const level = document.createElement('output');
              level.className = 'dd-zoom-level';
              level.setAttribute('aria-label', 'Zoom level');
              bar.appendChild(level);
              const plus = createButton('Zoom in', '+', () => zoom(1.5));
              const fit = createButton('Fit whole image', 'Fit image', () => zoom(0));
              fit.className = 'dd-zoom-fit';
              const update = () => {
                const slide = gallery.currSlide;
                if (!slide) return;
                level.textContent = `${(slide.currZoomLevel / slide.zoomLevels.initial).toFixed(1)}×`;
                minus.disabled = slide.currZoomLevel <= slide.zoomLevels.initial + 0.001;
                plus.disabled = slide.currZoomLevel >= slide.zoomLevels.max - 0.001;
                // aria-disabled keeps Fit image focusable after keyboard activation.
                fit.setAttribute('aria-disabled', String(minus.disabled));
              };
              gallery.on('zoomPanUpdate', update);
              gallery.on('change', update);
              element.append(help, bar);
            },
          });
          gallery.ui.registerElement({
            name: 'dd-announcement',
            appendTo: 'root',
            onInit: (element) => {
              element.className = 'dd-zoom-announcement';
              element.setAttribute('role', 'status');
              element.setAttribute('aria-live', 'polite');
              gallery.on('change', () => {
                element.textContent = `Photo ${gallery.currIndex + 1} of ${gallery.getNumItems()}`;
              });
            },
          });
        });
      }

      hide() {
        this.galleryRequest = null;
        if (this.gallery) {
          this.gallery.close();
          return;
        }
        if (this.hasAttribute('open')) super.hide();
      }

      showLegacy(opener) {
        const preview = opener.closest('modal-opener')?.querySelector('img');
        const image = Array.from(this.querySelectorAll('img[data-media-id]'))
          .find((element) => element.dataset.mediaId === opener.dataset.mediaId);
        if (image && preview?.complete && preview.naturalWidth) image.src = preview.currentSrc;
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        const activeMedia = Array.from(this.querySelectorAll('[data-media-id]'))
          .find((element) => element.dataset.mediaId === mediaId);
        if (!activeMedia) return;
        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });
        this.classList.toggle('dd-zoom-fallback', activeMedia.tagName === 'IMG');
        const template = activeMedia.querySelector('template');
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }
    }
  );
}
